import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, Stack, CircularProgress } from '@mui/material';
import {
    Description as DescriptionIcon,
    Article as ArticleIcon,
    FileDownload as FileDownloadIcon,
    Visibility as VisibilityIcon,
    InsertDriveFile as InsertDriveFileIcon,
    Image as ImageIcon,
    TableChart as TableChartIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { configuration } from 'utils/environment/configuration';
import { formatFileSize } from 'utils/fileUtils';
import { FilePreview } from './FilePreview';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';

// Document type mapping function
const getDocumentTypeName = (type) => {
    switch (type?.toUpperCase()) {
        case 'JUSTIFICATION':
            return 'Justification Documents';
        case 'REPORT':
            return 'Report Documents';
        case 'RECEIPT':
            return 'Receipt Documents';
        case 'ATTENDANCE':
            return 'Attendance Documents';
        case 'VEHICLE_LOG_SHEET':
            return 'Vehicle Log Sheet';
        case 'REFUND_DEPOSIT':
            return 'Refund Deposit Slip';
        case 'OTHER':
            return 'Other Documents';
        default:
            return 'Other Documents';
    }
};

// Document Handling Section for AcquittalPreviewDialog
const DocumentSection = ({ documents: propDocuments, theme, keycloak, requestId, pageParams = { page: 0, size: 10 } }) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentLoadingDoc, setCurrentLoadingDoc] = useState(null);
    const [localDocuments, setLocalDocuments] = useState(propDocuments || []);

    const { getTravelRequestDocuments, loading: apiLoading } = useTravelRequest();

    // Fetch documents when requestId is provided and merge with prop documents
    useEffect(() => {
        const fetchDocuments = async () => {
            let travelRequestDocuments = [];

            if (requestId) {
                try {
                    const data = await getTravelRequestDocuments(requestId, pageParams);
                    travelRequestDocuments = data.content || data || [];
                } catch (error) {
                    console.error('Error fetching travel request documents:', error);
                    travelRequestDocuments = [];
                }
            }

            // Merge travel request documents with prop documents (travel plan documents)
            const mergedDocuments = [...travelRequestDocuments, ...(propDocuments || [])];
            setLocalDocuments(mergedDocuments);
        };

        fetchDocuments();
    }, [requestId, pageParams.page, pageParams.size, propDocuments]);

    const handleDocumentPreview = async (documentId, fileName) => {
        try {
            setCurrentLoadingDoc(documentId);
            setIsLoading(true);

            const response = await fetch(`${configuration.apiUrl}/api/documents/${documentId}`, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch document: ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setSelectedDocument(fileName);
            setIsPreviewOpen(true);
        } catch (error) {
            console.error('Error previewing document:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load document preview',
                icon: 'error'
            });
        } finally {
            setIsLoading(false);
            setCurrentLoadingDoc(null);
        }
    };

    const handleDocumentDownload = async (documentId, fileName) => {
        try {
            setCurrentLoadingDoc(documentId);
            setIsLoading(true);

            const response = await fetch(`${configuration.apiUrl}/api/documents/${documentId}`, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch document: ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to download document',
                icon: 'error'
            });
        } finally {
            setIsLoading(false);
            setCurrentLoadingDoc(null);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setSelectedDocument(null);
    };

    const getFileIcon = (fileName) => {
        const extension = fileName?.toLowerCase().split('.').pop();
        switch (extension) {
            case 'pdf':
                return <ArticleIcon />;
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                return <ImageIcon />;
            case 'doc':
            case 'docx':
                return <DescriptionIcon />;
            case 'xls':
            case 'xlsx':
                return <TableChartIcon />;
            default:
                return <InsertDriveFileIcon />;
        }
    };

    const groupedDocuments = localDocuments.reduce((acc, doc) => {
        const type = doc.documentType || 'OTHER';
        if (!acc[type]) acc[type] = [];
        acc[type].push(doc);
        return acc;
    }, {});

    // Show loading state when fetching documents
    if (apiLoading && requestId) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show empty state when no documents
    if (!localDocuments || localDocuments.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    No documents found
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {Object.entries(groupedDocuments).map(([type, docs]) => (
                <Box key={type} sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ color: theme.primary.main, fontWeight: 600, mb: 2 }}>
                        {getDocumentTypeName(type)}
                    </Typography>
                    {docs.map((doc) => (
                        <Box
                            key={doc.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                mb: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                            }}
                        >
                            {getFileIcon(doc.fileName)}
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1">{doc.fileName || 'Unnamed Document'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(doc.fileSize)}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleDocumentPreview(doc.id, doc.fileName)}
                                    disabled={isLoading && currentLoadingDoc === doc.id}
                                >
                                    Preview
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={() => handleDocumentDownload(doc.id, doc.fileName)}
                                    disabled={isLoading && currentLoadingDoc === doc.id}
                                >
                                    Download
                                </Button>
                            </Stack>
                        </Box>
                    ))}
                </Box>
            ))}

            <FilePreview open={isPreviewOpen} onClose={handleClosePreview} fileUrl={previewUrl} fileName={selectedDocument} theme={theme} />
        </>
    );
};

// PropTypes for DocumentSection
DocumentSection.propTypes = {
    documents: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            fileName: PropTypes.string,
            fileSize: PropTypes.number,
            documentType: PropTypes.string
        })
    ),
    theme: PropTypes.object.isRequired,
    keycloak: PropTypes.object.isRequired,
    requestId: PropTypes.string,
    pageParams: PropTypes.shape({
        page: PropTypes.number,
        size: PropTypes.number
    })
};

export { FilePreview, DocumentSection };
